import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
} from "@mui/material";
import * as Yup from "yup";
import { BRAND_BLUE } from "../../shared/pricingHelpers";

export interface CurrencyCreditRateValues {
  currency: string;
  cost_per_credit: number | "";
  is_active: boolean;
}

const EMPTY: CurrencyCreditRateValues = {
  currency: "USD",
  cost_per_credit: "",
  is_active: true,
};

interface Props {
  open: boolean;
  mode: "create" | "edit";
  initial?: Partial<CurrencyCreditRateValues> | null;
  saving?: boolean;
  onClose: () => void;
  onSubmit: (payload: any) => void;
}

const schema = Yup.object().shape({
  currency: Yup.string().oneOf(["USD", "INR"]).required("Currency is required"),
  cost_per_credit: Yup.number()
    .typeError("Cost per credit is required")
    .required("Cost per credit is required")
    .moreThan(0, "Must be > 0"),
});

const buildPayload = (v: CurrencyCreditRateValues) => ({
  currency: v.currency,
  cost_per_credit: Number(v.cost_per_credit),
  is_active: v.is_active,
});

const CurrencyCreditRatesForm: React.FC<Props> = ({
  open,
  mode,
  initial,
  saving,
  onClose,
  onSubmit,
}) => {
  const [values, setValues] = useState<CurrencyCreditRateValues>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      setValues({ ...EMPTY, ...(initial || {}) } as CurrencyCreditRateValues);
      setErrors({});
    }
  }, [open, initial]);

  const set = (name: keyof CurrencyCreditRateValues, value: any) =>
    setValues((p) => ({ ...p, [name]: value }));

  const handleSubmit = async () => {
    try {
      await schema.validate(values, { abortEarly: false });
      setErrors({});
      onSubmit(buildPayload(values));
    } catch (err: any) {
      const e: Record<string, string> = {};
      err.inner?.forEach((x: any) => (e[x.path] = x.message));
      setErrors(e);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: BRAND_BLUE }}>
        {mode === "create" ? "Add Currency Credit Rate" : "Edit Currency Credit Rate"}
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
          <FormControl size="small" fullWidth>
            <InputLabel id="rate-currency">Currency</InputLabel>
            <Select
              labelId="rate-currency"
              label="Currency"
              value={values.currency}
              onChange={(e) => set("currency", e.target.value)}
            >
              <MenuItem value="USD">USD</MenuItem>
              <MenuItem value="INR">INR</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Cost per Credit"
            type="number"
            value={values.cost_per_credit}
            onChange={(e) =>
              set("cost_per_credit", e.target.value === "" ? "" : Number(e.target.value))
            }
            error={Boolean(errors.cost_per_credit)}
            helperText={errors.cost_per_credit || "Money value of 1 credit"}
            size="small"
            fullWidth
          />
          <FormControlLabel
            control={
              <Switch
                checked={values.is_active}
                onChange={(e) => set("is_active", e.target.checked)}
                color="primary"
              />
            }
            label="Active"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined" sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            backgroundColor: BRAND_BLUE,
            "&:hover": { backgroundColor: "#0445a3" },
          }}
        >
          {mode === "create" ? "Create" : "Save changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CurrencyCreditRatesForm;
